"use client";

import AnimatedHeading from "../ui/animatedHeading";
import ParallaxImageSection from "./imageSection";
import ScrollSection from "./scrollSection";
import HorizontalScrollSection from "./horizontalSection";
import HoloCard from "./holoCard";
import Link from "next/link";
import OutlineHeading from "../ui/outlineHeading";
import Image from "next/image";

// Add this constant at the top of your file, outside the component
const deckCards = [
  {
    id: 1,
    layoutClasses: "col-span-3 row-span-3",
    content: (
      <>
        Every hour,{" "}
        <Link href={"/"} className="underline text-purple-400">
          CatDevAI&apos;s
        </Link>{" "}
        engine generates evolution DNA&apos;s tokens—fresh contenders from a
        mind honed to conquer. They land unrefined on the platform, where the
        community eagerly votes for the one that makes the strongest impression.
        The victor ascends to Solana&apos;s pump.fun, marking its name on the
        leaderboard as a testament to the cat&apos;s triumph.
      </>
    ),
  },
  {
    id: 2,
    showImage: true,
    layoutClasses: "col-span-2 row-span-6 col-start-4",
    content:
      "Every thirty minutes, the cat assesses the ranks, searching for weakness. Each token undergoes a rigorous test—engagement, liquidity, and raw appeal. Failure leads to erasure, leaving only the robust tokens to stand, their worth etched into the blockchain's core. This process is not born of kindness; it's the catdevai engine refining its pack.",
  },
  {
    id: 3,
    layoutClasses: "col-span-3 row-span-3 row-start-4",
    content:
      "A resilient token never rests—it evolves. The cat passes on its legacy, with each new token shaped by survival and experience. This isn't random; it's a lineage built through natural selection, growing stronger with each cycle. The cycle persists, each branch proving struggle and victory.",
  },
  {
    id: 4,
    layoutClasses: "col-span-5 row-span-2 row-start-7",
    content:
      "Darwin's cat is not here to play; it's a forge where only the fiercest meme tokens rise. Theory converges with strength: variation in prompts, selection through votes, and legacy in the roots. The cat is not merely observing; it's orchestrating the storm, one relentless round at a time",
  },
];

// Add these constants near your other constants (outside the component)
const mainPrizeCard = {
  title: "This isn't a gamble.",
  imageSrc: "/docs/section-7/not-cards.png",
  imageAlt: "prize",
  content:
    "This isn't a gamble. Darwin's cat operates a proving ground—weak tokens perish quickly. Victory requires courage: quality, momentum, growth. This isn't about risking your savings; it's about observing the cat's machine sift the gold from the mud to build a meme empire with bite.",
};

const prizeCards = [
  {
    id: 1,
    imageSrc: "/docs/section-7/prize-cup.png",
    imageAlt: "prize",
    content:
      "The cat churns out legions of tokens, each a wild card on its own journey. Diversity fuels the system, continuously refining them to their essence. The toughest will ascend, redefining the potential of AI-born memes.",
    imagePosition: "left",
    glowPosition: { left: "0px", top: "-20%" },
  },
  {
    id: 2,
    imageSrc: "/docs/section-7/cat-card.png",
    imageAlt: "prize",
    content: (
      <>
        You&apos;re witnessing evolution in motion. This isn&apos;t a rehearsal—
        <span className="text-purple-400 font-bold underline">
          <Link href={"/"}>CatDevAI</Link>
        </span>{" "}
        is a living force, applying natural selection on a digital stage. Study
        the cat&apos;s craft and observe how the fittest tokens ascend their
        throne
      </>
    ),
    imagePosition: "right",
    glowPosition: { right: "0px", top: "-20%" },
  },
  {
    id: 3,
    imageSrc: "/docs/section-7/cat-tech.png",
    imageAlt: "prize",
    content:
      "It's accelerating meme evolution, period. Darwin's cat isn't just creating memes—it's breeding them with technology that mirrors life's hard rules. The future belongs to self-made, viral tokens, forged in a system that never sleeps",
    imagePosition: "left",
    glowPosition: { left: "0px", top: "-20%" },
  },
  {
    id: 4,
    imageSrc: "/docs/section-7/cat-code.png",
    imageAlt: "prize",
    content:
      "The cat doesn't aim to please—it wields code and chaos to birth a new breed of digital titans. Theory is the guide: selection, adaptation, refinement. The technology is the force making it happen",
    imagePosition: "right",
    glowPosition: { right: "0px", top: "-20%" },
    extraImageClass: "px-2",
  },
];

const ParallaxPage = () => {
  return (
    <>
      <HorizontalScrollSection />

      <div id="parallax-world">
        <section>
          <ParallaxImageSection src="/docs/CatDev-6.jpg" alt="Enchanted Forest">
            <AnimatedHeading
              as="div"
              playOnView
              splitText
              delayStep={0.15}
              duration={0.5}
              className="mb-12 mt-16 text-white font-bold"
              innerClassName="text-2xl md:text-5xl"
            >
              <OutlineHeading className="max-w-screen-lg">
                CatDevAI: Where Darwin&apos;s Cat Hones the Blade
              </OutlineHeading>
            </AnimatedHeading>
          </ParallaxImageSection>
          <ScrollSection
            direction="left"
            alt="Legacy image"
            tagline="Votes, and legacy in the roots"
            text={`This is Darwin&apos;s cat in action—CatDevAI, a force of evolution and rebirth, driven by technology and the instinct to thrive. It&apos;s not a playground; it&apos;s a coliseum where only the fittest prevail. 
              
            Every token must prove its mettle—there&apos;s no place for the weak. The crowd&apos;s vote is the first cut, selecting the most impactful. Then comes the DNA gauntlet—those with a weak spark or no roar are snu ed out. The survivors don&apos;t rest; they reproduce, driving a cycle that hones the breed with every turn. It&apos;s evolution, not charity`}
            title="There's no place for the weak"
          />
        </section>

        <section className="relative z-0 flex flex-col justify-center items-center my-16 p-2 md:p-6 ">
          <OutlineHeading className="max-w-screen-lg">
            {" "}
            CatDevAI: How Darwin&apos;s Cat Stacks the Deck ?
          </OutlineHeading>
          <div className="flex flex-col md:grid grid-cols-5 grid-rows-8 gap-3 max-w-screen-lg text-indigo-50 text-base md:text-lg">
            {deckCards.map((card) => (
              <div
                key={card.id}
                className={`h-full w-full backdrop-blur-lg rounded-xl p-6 text-justify leading-relaxed relative bg-purple-950/5 transition-all ${card.layoutClasses}`}
              >
                <p className="relative">
                  <span className="float-left mr-4 font-black text-5xl lg:text-7xl text-transparent bg-clip-text bg-gradient-to-b from-purple-300 via-purple-500 to-transparent opacity-90 leading-none">
                    {card.id}
                  </span>
                  {card.showImage && (
                    <Image
                      className={`float-end w-1/2 h-auto md:h-auto hover:scale-110 transition-transform`}
                      height={500}
                      width={500}
                      alt={"cat-deck"}
                      src={"/docs/cat-deck.png"}
                    />
                  )}
                  {card.content}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Parallax Seven */}
        <section className="relative z-0 flex justify-center items-center p-2 md:p-6  my-16">
          <div className="max-w-screen-lg flex flex-col items-center justify-center text-white text-justify">
            <OutlineHeading>What&apos;s the Prize ?</OutlineHeading>
            <div className="flex flex-col md:flex-row gap-3">
              <div>
                <HoloCard className="h-full w-full md:w-96 bg-purple-600/10">
                  <div className="flex flex-col items-center h-full">
                    <OutlineHeading className="text-center w-full lg:text-4xl text-transparent bg-clip-text bg-gradient-to-b from-purple-300 to-[#913cff] mb-1">
                      {mainPrizeCard.title}
                    </OutlineHeading>
                    <Image
                      className="w-auto h-auto"
                      height={200}
                      width={200}
                      alt={mainPrizeCard.imageAlt}
                      src={mainPrizeCard.imageSrc}
                    />
                    <p> {mainPrizeCard.content}</p>
                  </div>
                </HoloCard>
              </div>
              <ul className="flex flex-col gap-3 text-base">
                {prizeCards.map((card) => (
                  <li key={card.id}>
                    <HoloCard
                      defaultGlowPosition={card.glowPosition}
                      className="md:min-h-fit min-h-fit bg-purple-600/10"
                    >
                      <div
                        className={`flex relative ${
                          card.imagePosition === "right"
                            ? "flex-row-reverse"
                            : ""
                        }`}
                      >
                        {" "}
                        <Image
                          className={`hidden xl:flex w-auto h-auto hover:scale-110 transition-transform ${
                            card.extraImageClass || ""
                          }`}
                          height={80}
                          width={80}
                          alt={card.imageAlt}
                          src={card.imageSrc}
                        />
                        <p className="relative">
                          {" "}
                          <Image
                            className={`float-left xl:hidden w-auto h-1/2 mb-1 md:h-auto hover:scale-110 transition-transform ${
                              card.extraImageClass || ""
                            }`}
                            height={80}
                            width={80}
                            alt={card.imageAlt}
                            src={card.imageSrc}
                          />{" "}
                          {card.content}
                        </p>
                      </div>
                    </HoloCard>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ParallaxPage;
